import { SPATEM } from '_lib/api';
import { RootState } from '_store';
import { DialogReturnTarget, DialogType } from '_store/application/types';
import { useLocalization } from '@fluent/react';
import { CardContent, Grid, Typography } from '@material-ui/core';
import React, { FC } from 'react';
import { useSelector } from 'react-redux';

import AssociatedRsus from '../../../items/AssociatedRsus';
import styles from '../../Device/Card/styles';

export interface SpatemCardDetailsProps {
  spatem: SPATEM;
}

// Status bit positions and their corresponding keys
const STATUS_BITS = {
  0: 'manualControlIsEnabled',
  1: 'stopTimeIsActivated',
  2: 'failureFlash',
  3: 'preemptIsActive',
  4: 'signalPriorityIsActive',
  5: 'fixedTimeOperation',
  6: 'trafficDependentOperation',
  7: 'standbyOperation',
  8: 'failureMode',
  9: 'off',
  10: 'recentMAPmessageUpdate',
  11: 'recentChangeInMAPassignedLanesIDsUsed',
  12: 'noValidMAPisAvailableAtThisTime',
  13: 'noValidSPATisAvailableAtThisTime',
  14: 'unknownStatus',
  15: 'unknownStatus',
} as const;

const beautifySpatemStatus = (
  statusHex: string | number | undefined,
  debug = false,
): React.ReactElement => {
  if (!statusHex) {
    return <span>-</span>;
  }

  try {
    // Convert hex string to number
    const statusValue = typeof statusHex === 'string' ? parseInt(statusHex, 16) : statusHex;

    if (Number.isNaN(statusValue)) {
      return <span>-</span>;
    }

    // Get active status keys
    // Note: Bits are indexed from left to right (MSB = bit 0, LSB = bit 15)
    // So we need to convert left-to-right index to right-to-left for bitwise check
    const activeStatuses: string[] = [];

    Object.entries(STATUS_BITS).forEach(([bitStr, key]) => {
      const bitFromLeft = parseInt(bitStr, 10);
      // Convert left-to-right index to right-to-left (LSB first) for bitwise operation
      // For 16-bit: bit 0 (left) = bit 15 (right), bit 5 (left) = bit 10 (right)
      const bitFromRight = 15 - bitFromLeft;
      // Check if the bit is set (1) at the converted position
      // eslint-disable-next-line no-bitwise
      if (statusValue & (1 << bitFromRight)) {
        activeStatuses.push(key);
      }
    });

    // Format binary string with spaces every 4 bits for readability
    const binaryString = statusValue.toString(2).padStart(16, '0');
    const formattedBinary = binaryString.match(/.{1,4}/g)?.join(' ') || binaryString;

    // Format hex string
    const hexString = `0x${statusValue.toString(16).toUpperCase().padStart(4, '0')}`;

    if (activeStatuses.length === 0) {
      if (debug) {
        return (
          <div>
            <div>Hex: {hexString}</div>
            <div>Bin: {formattedBinary}</div>
            <span>-</span>
          </div>
        );
      }
      return <span>-</span>;
    }

    return (
      <div>
        {debug && (
          <div style={{ marginBottom: '8px', fontSize: '0.9em', color: '#666' }}>
            <div>Hex: {hexString}</div>
            <div>Bin: {formattedBinary}</div>
          </div>
        )}
        <ul style={{ margin: 0, paddingLeft: '20px' }}>
          {activeStatuses.map((status) => (
            <li key={status}>{status}</li>
          ))}
        </ul>
      </div>
    );
  } catch {
    return <span>-</span>;
  }
};

const getSpatemReturnTarget = (
  currentDialog: { type?: DialogType } | null | undefined,
  spatem: SPATEM,
): DialogReturnTarget => ({
  type: currentDialog?.type === DialogType.SINGLE_SPATEM ? DialogType.SINGLE_SPATEM : DialogType.SPATEM_LIST,
  id: spatem.id,
  coordinates: spatem.location?.coordinates,
  zoom: 15,
});

const SpatemCardDetails: FC<SpatemCardDetailsProps> = ({ spatem }) => {
  const classes = styles();
  const { l10n } = useLocalization();
  const dialog = useSelector((state: RootState) => state.application.activeDialog);

  const showTextOrHyphen = (text: string | number | undefined) => (text ? String(text) : '-');

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch {
      return dateString;
    }
  };

  const lat = spatem?.location?.coordinates?.[1];
  const lng = spatem?.location?.coordinates?.[0];

  return (
    <CardContent className={classes.detailStyle}>
      <Grid container spacing={1} style={{ marginTop: 10, paddingRight: 20 }}>
        <Grid item xs={12} className={classes.gridVertical}>
          <Typography component={'span'} variant={'body2'} paragraph className={classes.textStyle}>
            {l10n.getString('forms-info-box-identifier')}{' '}
            <strong>{`\n ${showTextOrHyphen(spatem?.identifier)}`}</strong>
          </Typography>

          <Typography variant="caption" color="textSecondary" display="block">
            {l10n.getString('forms-spatem-detail-rsus')}
          </Typography>
          <div style={{ marginBottom: 10 }}>
            <AssociatedRsus
              rsus={spatem?.payload?.rsus ?? []}
              returnTo={getSpatemReturnTarget(dialog, spatem)}
            />
          </div>

          <Grid container direction="row" alignItems="flex-start" className={classes.innerGrid}>
            <Grid item xs={12} sm={8}>
              <Typography
                component={'span'}
                variant={'body2'}
                paragraph
                className={classes.textStyle}
              >
                {l10n.getString('forms-spatem-lat')}{' '}
                <strong>{`\n ${showTextOrHyphen(lat)}`}</strong>
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography
                component={'span'}
                variant={'body2'}
                paragraph
                className={classes.textStyle}
              >
                {l10n.getString('forms-spatem-long')}{' '}
                <strong>{`\n ${showTextOrHyphen(lng)}`}</strong>
              </Typography>
            </Grid>
          </Grid>

          <Typography component={'span'} variant={'body2'} paragraph>
            {l10n.getString('forms-spatem-detail-status')}
            <strong>{beautifySpatemStatus(spatem?.payload?.status)}</strong>
          </Typography>

          <Grid container direction="row" alignItems="flex-start" className={classes.innerGrid}>
            <Grid item xs={12} sm={8}>
              <Typography variant={'body2'} paragraph className={classes.textStyle}>
                {l10n.getString('forms-spatem-moy')}{' '}
                <strong>{`\n ${showTextOrHyphen(spatem?.payload?.moy)}`}</strong>
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant={'body2'} paragraph className={classes.textStyle}>
                {l10n.getString('forms-spatem-timestamp')}{' '}
                <strong>{`\n ${formatDate(spatem?.payload?.timestamp)}`}</strong>
              </Typography>
            </Grid>
          </Grid>

          <Typography variant={'body2'} paragraph className={classes.textStyle}>
            {l10n.getString('forms-spatem-mapem-timestamp')}{' '}
            <strong>{`\n ${formatDate(spatem?.payload?.mapem_timestamp)}`}</strong>
          </Typography>
          <Typography variant={'body2'} paragraph className={classes.textStyle}>
            {l10n.getString('forms-spatem-created-at')}{' '}
            <strong>{`\n ${formatDate(spatem?.created_at)}`}</strong>
          </Typography>
          <Typography variant={'body2'} paragraph className={classes.textStyle}>
            {l10n.getString('forms-spatem-updated-at')}{' '}
            <strong>{`\n ${formatDate(spatem?.updated_at)}`}</strong>
          </Typography>
        </Grid>
      </Grid>
    </CardContent>
  );
};

export default React.memo(SpatemCardDetails);
